import type { Badge } from '@moment/design-system-ui/badge'
import type { PopoverProps } from '@radix-ui/react-popover'

import { composeRefs } from '@radix-ui/react-compose-refs'
import { useControllableState } from '@radix-ui/react-use-controllable-state'
import { Command as CommandPrimitive, useCommandState } from 'cmdk'
import {
  type ComponentPropsWithoutRef,
  createContext,
  type ElementRef,
  forwardRef,
  type HTMLProps,
  isValidElement,
  type ReactNode,
  type RefObject,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import Checkbox from '@/components/atomic/checkbox'
import Icon, { IconName } from '@/components/atomic/icon'
import { BodySmall, BodySubtext, BodyXS } from '@/components/atomic/text'
import {
  Command,
  CommandEmpty,
  CommandItem,
  CommandList,
} from '@/components/shadcn/Command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/shadcn/Popover'
import { cn, useContextSafely } from '@/utils/shadcn/utils'

interface SlotProps {
  label?: string
  icon?: ReactNode
  side?: 'left' | 'right'
  showSeparator?: boolean
  separatorHeight?: string
  size?: 'small' | 'large'
  className?: string
}

function generateSlot(
  props: SlotProps,
  ref: RefObject<HTMLDivElement | null>,
  rightPadding: number = 0,
  inputWidth: number = 0,
) {
  const label = props.label ? (
    // biome-ignore lint/style/noNestedTernary: Existing code — enable rule without runtime changes
    props.size === 'small' ? (
      <BodySubtext color={'text-secondary'} className={props.className}>
        {props.label}
      </BodySubtext>
    ) : (
      <BodySmall color={'text-secondary'} className={props.className}>
        {props.label}
      </BodySmall>
    )
  ) : null
  return (
    <div
      className={cn(
        'absolute px-3 py-1.5',
        props.side === 'left' && 'border-r border-primary-border',
      )}
      ref={ref}
      style={{
        left:
          props && props.side === 'right'
            ? `${inputWidth - rightPadding}px`
            : undefined,
      }}
    >
      <div className="relative flex items-center justify-center whitespace-nowrap">
        {props.icon ? <div className="w-4 h-4">{props.icon}</div> : label}
        {props.showSeparator && (
          <div
            className={`absolute w-[1px] bg-moment-blue-grey-200 dark:bg-moment-blue-grey-1000`}
            style={{
              right: props && props.side === 'left' ? '-12px' : undefined,
              height: props.separatorHeight,
            }}
          />
        )}
      </div>
    </div>
  )
}

const MultiselectCommandInput = forwardRef<
  ElementRef<typeof CommandPrimitive.Input>,
  ComponentPropsWithoutRef<typeof CommandPrimitive.Input>
>(({ className, ...props }, ref) => {
  return (
    <div className="flex items-center border-b border-primary-border px-3">
      <CommandPrimitive.Input
        ref={ref}
        className={cn(
          'flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-hidden placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...props}
      />
    </div>
  )
})

MultiselectCommandInput.displayName = 'MultiselectCommandInput'

interface MultiselectContextApi {
  selectionSet: Set<string>
  selection: string[] | undefined
  select: (val: string) => void
  unselect: (val: string) => void
  open: boolean
  setOpen: (val: boolean) => void
  disabled?: boolean
}

const MultiselectContext = createContext<MultiselectContextApi | null>(null)

interface MultiselectProps extends PopoverProps {
  value?: string[]
  onValueChange?: (value: string[]) => void
  defaultValue?: string[]
  className?: string
  disabled?: boolean
}

export const Multiselect = forwardRef<HTMLDivElement, MultiselectProps>(
  (
    {
      defaultOpen = false,
      open,
      onOpenChange,
      defaultValue = [],
      value,
      onValueChange,
      children,
      disabled = false,
      ...otherProps
    },
    _ref,
  ) => {
    const [_selection, _setSelection] = useControllableState({
      prop: value,
      defaultProp: defaultValue,
      onChange: onValueChange,
    })

    const [_open, _setOpen] = useControllableState({
      prop: open,
      defaultProp: defaultOpen,
      onChange: onOpenChange,
    })

    const selectionSet = useMemo(() => new Set(_selection), [_selection])

    // ref to track click target
    const clickTargetRef = useRef<HTMLElement | null>(null)

    // Event listener to track mousedown target
    useEffect(() => {
      const handleMouseDown = (e: MouseEvent) => {
        clickTargetRef.current = e.target as HTMLElement
      }

      document.addEventListener('mousedown', handleMouseDown)
      return () => document.removeEventListener('mousedown', handleMouseDown)
    }, [])

    const handleOpenChange = (open: boolean) => {
      if (disabled) return

      // Radix UI's onOpenChange doesn't provide the event directly,
      // so we get it from window.event to check what was clicked
      const target = clickTargetRef.current
      clickTargetRef.current = null // Reset after use

      // Fallback:
      if (!target) {
        _setOpen(open)
        onOpenChange?.(open)
        return
      }

      // Check if the click target is a MultiselectBadge
      const isMultiselectBadgeClick =
        target.closest('.multiselectBadge') !== null

      // Only trigger open change if it's not a badge click
      if (!isMultiselectBadgeClick) {
        _setOpen(open)
        onOpenChange?.(open)
      }
    }

    return (
      <MultiselectContext.Provider
        value={{
          selectionSet,
          selection: _selection,
          select: (value: string) => {
            if (disabled) return
            _setSelection((prev) => Array.from(new Set([...prev, value])))
          },
          unselect: (value: string) => {
            if (disabled) return
            _setSelection((prev) => prev.filter((v) => v !== value))
          },
          open: _open,
          setOpen: (val: boolean) => {
            if (disabled) return
            _setOpen(val)
          },
          disabled,
        }}
      >
        <Popover
          {...otherProps}
          open={disabled ? false : _open}
          onOpenChange={handleOpenChange}
        >
          {children}
        </Popover>
      </MultiselectContext.Provider>
    )
  },
)

Multiselect.displayName = 'Multiselect'

interface MultiselectTriggerApi {
  grow: boolean
}

const MultiselectTriggerContext = createContext<MultiselectTriggerApi | null>(
  null,
)

interface MultiselectTriggerProps
  extends ComponentPropsWithoutRef<typeof PopoverTrigger> {
  grow?: boolean
  placeholder?: string
  slotProps?: SlotProps
  size?: 'small' | 'large'
}

export const MultiselectTrigger = forwardRef<
  ElementRef<typeof PopoverTrigger>,
  MultiselectTriggerProps
>(
  (
    {
      children,
      className,
      placeholder = 'Select...',
      grow = false,
      slotProps,
      size = 'large',
      ...otherProps
    },
    ref,
  ) => {
    const multiselect = useContextSafely(MultiselectContext)
    const mask = grow
      ? undefined
      : 'linear-gradient(90deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 1) calc(100% - 12px), rgba(255, 255, 255, 0) 100%)'

    const leftSlotRef = useRef<HTMLDivElement | null>(null)
    const inputRef = useRef<HTMLDivElement | null>(null)

    const [leftPadding, setLeftPadding] = useState<number>(0)
    const [inputWidth, setInputWidth] = useState<number>(0)

    useLayoutEffect(() => {
      if (
        leftSlotRef.current &&
        leftSlotRef.current.offsetWidth !== leftPadding
      ) {
        setLeftPadding(leftSlotRef.current.offsetWidth)
      }
      if (inputRef.current && inputRef.current.offsetWidth !== inputWidth) {
        setInputWidth(inputRef.current.offsetWidth)
      }
    }, [leftPadding, inputWidth])

    return (
      <MultiselectTriggerContext.Provider value={{ grow }}>
        <PopoverTrigger
          {...otherProps}
          ref={ref}
          className={cn(
            `relative flex items-center w-full whitespace-nowrap overflow-hidden rounded-[6px] border border-primary-border bg-transparent text-sm shadow-none placeholder:text-muted-foreground`,
            `hover:border-moment-teal-400 dark:hover:border-moment-teal-800
            focus:outline-hidden focus:ring-2 focus:border-1
            focus:ring-moment-teal-500 focus:border-moment-teal-600`,
            `data-[state=open]:outline-hidden data-[state=open]:ring-2 data-[state=open]:border-1
            data-[state=open]:ring-moment-teal-500 data-[state=open]:border-moment-teal-600
            dark:data-[state=open]:ring-moment-teal-900 dark:data-[state=open]:border-moment-teal-500
            dark:focus:ring-moment-teal-900 dark:focus:border-moment-teal-500`,
            `&>span]:line-clamp-1`,
            size === 'small' && (grow ? 'min-h-[28px]' : 'h-[28px]'),
            size === 'large' && (grow ? 'min-h-[32px]' : 'h-[32px]'),
            multiselect.disabled
              ? `opacity-50 cursor-not-allowed`
              : 'cursor-pointer',
            className,
          )}
          disabled={multiselect.disabled}
        >
          {slotProps &&
            slotProps.side === 'left' &&
            generateSlot(slotProps, leftSlotRef)}

          <div
            className={cn(
              'relative flex-1 h-auto rounded-l-[inherit] min-w-0 pl-[5px] pr-[12px] py-[5px] overflow-auto scrollbar-hide',
            )}
            ref={inputRef}
            style={{
              mask,
              WebkitMask: mask,
              paddingLeft:
                slotProps?.side === 'left'
                  ? `${leftPadding + (slotProps.showSeparator ? 12 : 0)}px`
                  : undefined,
            }}
          >
            {multiselect.selection?.length === 0 ? (
              <div className="px-0.5 h-6 flex items-center">
                <BodySubtext>{placeholder}</BodySubtext>
              </div>
            ) : (
              children
            )}
          </div>

          <div className="rounded-r-[inherit] pl-0 pr-2 bg-transparent">
            {multiselect.open ? (
              <Icon iconName={IconName.ChevronUpIcon} size="medium" />
            ) : (
              <Icon iconName={IconName.ChevronDownIcon} size="medium" />
            )}
          </div>
        </PopoverTrigger>
      </MultiselectTriggerContext.Provider>
    )
  },
)

MultiselectTrigger.displayName = 'MultiselectTrigger'

export const MultiselectBadgeList = forwardRef<
  HTMLDivElement,
  HTMLProps<HTMLDivElement>
>(({ children, className, ...otherProps }, ref) => {
  const trigger = useContextSafely(MultiselectTriggerContext)
  return (
    <div
      ref={ref}
      className={cn(
        'flex gap-1 flex-wrap ml-[-2px]',
        !trigger.grow && 'w-max flex-nowrap',
        className,
      )}
      {...otherProps}
    >
      {children}
    </div>
  )
})

MultiselectBadgeList.displayName = 'MultiselectBadgeList'

interface MultiselectBadgeProps extends ComponentPropsWithoutRef<typeof Badge> {
  value: string
  badgeClassName?: string
}

export const MultiselectBadge = forwardRef<
  HTMLDivElement,
  MultiselectBadgeProps
>(({ children, value, badgeClassName, ...otherProps }, ref) => {
  const multiselect = useContextSafely(MultiselectContext)

  return (
    // biome-ignore lint/a11y/useSemanticElements: Bulk suppression as part of biome migration
    <div
      ref={ref}
      tabIndex={0}
      role="button"
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          multiselect.unselect(value)
        }
      }}
      onClick={(e) => {
        e.stopPropagation()
        if (!multiselect.disabled) {
          multiselect.unselect(value)
        }
      }}
      className={cn(
        'multiselectBadge', // Class for badge detection
        'border border-primary-border rounded-sm pl-[5px] pr-[4px] py-2 h-[20px] flex items-center justify-between max-w-24 cursor-pointer',
        badgeClassName,
        multiselect.disabled && 'cursor-not-allowed',
      )}
      {...otherProps}
    >
      <div className="flex items-center gap-1 w-full">
        <BodyXS className="text-ellipsis overflow-hidden">{children}</BodyXS>
        <Icon
          iconName={IconName.XMarkIcon}
          className="flex-shrink-0"
          size="small"
        />
      </div>
    </div>
  )
})

MultiselectBadge.displayName = 'MultiselectBadge'

export const MultiselectInput = forwardRef<
  ElementRef<typeof MultiselectCommandInput>,
  ComponentPropsWithoutRef<typeof MultiselectCommandInput>
>(
  (
    {
      placeholder = 'Search...',
      value,
      defaultValue,
      onValueChange,
      ...otherProps
    },
    forwardedRef,
  ) => {
    const ref = useRef<HTMLInputElement>(null)
    const multiselect = useContextSafely(MultiselectContext)
    const content = useContextSafely(MultiselectContentContext)
    const activeValue = useCommandState((state) => state.value)

    const [search, setSearch] = useControllableState({
      prop: value,
      defaultProp: defaultValue as string,
      onChange: onValueChange,
    })

    useEffect(() => {
      if (multiselect.open) {
        setSearch('')
      }
    }, [multiselect.open, setSearch])

    useEffect(() => {
      if (ref.current) {
        const activeItemEl = content.rootRef.current?.querySelector(
          `[cmdk-item=""][data-value="${encodeURIComponent(activeValue)}"]`,
        )
        if (activeItemEl) {
          ref.current.setAttribute('aria-activedescendant', activeItemEl.id)
        }
      }
    }, [activeValue, content.rootRef])

    return (
      <div
        className="mb-1 -mt-1"
        role="presentation" // Add a non-interactive role for the wrapper
      >
        <MultiselectCommandInput
          ref={forwardedRef ? composeRefs(forwardedRef, ref) : ref}
          placeholder={placeholder}
          value={search}
          onValueChange={setSearch}
          className="h-9 px-1.5 text-sm"
          onKeyDown={(e) => {
            // Add keyboard interaction
            if (e.key === 'Enter') {
              e.preventDefault()
              // Trigger the same action as a click or selection logic
            }
          }}
          onBlur={(e) => {
            e.currentTarget.focus({ preventScroll: true })
          }}
          role="searchbox"
          aria-disabled={multiselect.disabled}
          tabIndex={0}
          disabled={multiselect.disabled}
          {...otherProps}
        />
      </div>
    )
  },
)

MultiselectInput.displayName = 'MultiselectInput'

const AriaDescendantFix = ({
  listRef,
}: {
  listRef: RefObject<HTMLDivElement>
}) => {
  const activeValue = useCommandState((state) => state.value)

  useEffect(() => {
    if (listRef.current) {
      const activeItemEl = listRef.current?.querySelector(
        `[cmdk-item=""][data-value="${encodeURIComponent(activeValue)}"]`,
      )
      if (activeItemEl) {
        listRef.current.setAttribute('aria-activedescendant', activeItemEl.id)
      }
    }
  }, [activeValue, listRef])

  return null
}

AriaDescendantFix.displayName = 'AriaDescendantFix'

interface MultiselectContentApi {
  rootRef: RefObject<HTMLDivElement>
}

const MultiselectContentContext = createContext<MultiselectContentApi | null>(
  null,
)

export const MultiselectContent = forwardRef<
  ElementRef<typeof PopoverContent>,
  ComponentPropsWithoutRef<typeof PopoverContent> & {
    placeholder?: string
    filter?: (value: string, search: string) => number
  }
>(({ children, className, align = 'end', filter, ...otherProps }, ref) => {
  const rootRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  return (
    <MultiselectContentContext.Provider
      value={{ rootRef: rootRef as RefObject<HTMLDivElement> }}
    >
      <PopoverContent
        ref={ref}
        className={cn(
          'p-1 w-[var(--radix-popover-trigger-width)] bg-background dark:bg-moment-black-200',
          className,
        )}
        align={align}
        asChild
        {...otherProps}
      >
        <Command
          ref={rootRef}
          className={cn('overflow-visible', className)}
          role="listbox"
          filter={filter}
        >
          <AriaDescendantFix listRef={rootRef as RefObject<HTMLDivElement>} />
          <CommandList ref={listRef as RefObject<HTMLDivElement>}>
            {children}
          </CommandList>
        </Command>
      </PopoverContent>
    </MultiselectContentContext.Provider>
  )
})

MultiselectContent.displayName = 'MultiselectContent'

interface MultiselectItemProps
  extends ComponentPropsWithoutRef<typeof CommandItem> {
  value: string
  rightContent?: ReactNode
  subtext?: string
}

export const MultiselectItem = forwardRef<
  ElementRef<typeof CommandItem>,
  MultiselectItemProps
>(
  (
    {
      children,
      className,
      value,
      onSelect,
      rightContent,
      subtext,
      ...otherProps
    },
    ref,
  ) => {
    const multiselect = useContextSafely(MultiselectContext)
    const checked = multiselect.selectionSet.has(value)

    const renderContent = (content: ReactNode | string) => {
      if (isValidElement(content)) return content
      return (
        <BodySubtext color="text-moment-blue-grey-800">{content}</BodySubtext>
      )
    }

    return (
      <CommandItem
        ref={ref}
        role="option"
        value={value}
        onSelect={(value) => {
          if (multiselect.disabled) return
          if (multiselect.selectionSet.has(value)) {
            multiselect.unselect(value)
          } else {
            multiselect.select(value)
          }
          onSelect?.(value)
        }}
        className={cn(
          'flex flex-col gap-0.5',
          className,
          multiselect.disabled && 'cursor-not-allowed opacity-50',
        )}
        aria-checked={checked}
        {...otherProps}
      >
        <div className="flex items-center justify-between w-full cursor-pointer">
          <div className="flex flex-col min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <Checkbox checked={checked} disabled={multiselect.disabled} />
              {children}
            </div>
            {subtext && <div className="pl-6">{renderContent(subtext)}</div>}
          </div>
          {rightContent && renderContent(rightContent)}
        </div>
      </CommandItem>
    )
  },
)

MultiselectItem.displayName = 'MultiselectItem'

export const MultiselectEmpty = forwardRef<
  ElementRef<typeof CommandEmpty>,
  ComponentPropsWithoutRef<typeof CommandEmpty>
>(({ children, ...otherProps }, ref) => {
  return (
    <CommandEmpty ref={ref} {...otherProps}>
      {children}
    </CommandEmpty>
  )
})

MultiselectEmpty.displayName = 'MultiselectEmpty'
